import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PlusSquare, RotateCcw, Check, Tag, AlertTriangle, RefreshCw } from "lucide-react";
import PdaLayout from "./PdaLayout";
import { beep, primeAudio } from "@/lib/pdaSounds";
import { usePdaScan } from "@/hooks/usePdaScan";
import usePdaScenarioHint from "@/hooks/usePdaScenarioHint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Register fresh linen. Workflow:
 *   1. Pick the product type the operator is about to tag.
 *   2. Scan a stack of RFIDs. Tags ALREADY in the system are detected live
 *      (shown with their existing product info) and are KEPT untouched by
 *      default — unless the operator taps one to mark it "overwrite".
 *   3. Add an optional note, then Register.
 */
export default function PdaRegister({ onLogout }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState(null);
  const [rfids, setRfids] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // rfid -> existing item info (from lookup); null means "known new"
  const [existingInfo, setExistingInfo] = useState({});
  const [overwriteSet, setOverwriteSet] = useState(() => new Set());

  usePdaScenarioHint("Register");
  useEffect(() => { primeAudio(); }, []);

  useEffect(() => {
    axios.get(`${API}/products`)
      .then((res) => setProducts((res.data || []).filter((p) => !p.is_archived)))
      .catch(() => toast.error("Couldn't load products"))
      .finally(() => setProductsLoading(false));
  }, []);

  const rfidsRef = useRef(new Set());
  // Cache of rfids we've already looked up this batch — MUST be reset on
  // clear/submit, otherwise a rescan of the same tags skips the lookup and
  // everything shows as "new".
  const lookedUpRef = useRef(new Set());
  const clearRfids = useCallback(() => {
    rfidsRef.current = new Set();
    lookedUpRef.current = new Set();
    setRfids(new Set());
    setExistingInfo({});
    setOverwriteSet(new Set());
  }, []);

  const onEpcs = useCallback((epcs) => {
    if (!epcs || epcs.length === 0) return;
    let added = 0;
    for (const e of epcs) {
      if (!rfidsRef.current.has(e)) { rfidsRef.current.add(e); added++; }
    }
    if (added > 0) {
      setRfids(new Set(rfidsRef.current));
      beep.tick();
    }
  }, []);
  usePdaScan(onEpcs, { enabled: !!productId });

  // Live lookup: ~600ms after the last scan, resolve which tags already exist
  // so the operator sees it immediately (not after submitting).
  useEffect(() => {
    const pending = Array.from(rfids).filter((r) => !lookedUpRef.current.has(r));
    if (pending.length === 0) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/linen/lookup-many`, { rfids: pending });
        const next = {};
        for (const it of res.data.items || []) next[it.rfid] = it;
        for (const r of res.data.unknown || []) next[r] = null;
        for (const r of pending) lookedUpRef.current.add(r);
        setExistingInfo((prev) => ({ ...prev, ...next }));
      } catch { /* retried on next scan */ }
    }, 600);
    return () => clearTimeout(t);
  }, [rfids]);

  const product = useMemo(() => products.find((p) => p.id === productId) || null, [products, productId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [products, search]);

  const { newTags, existingTags, pendingCount } = useMemo(() => {
    const nt = [], et = [];
    let pending = 0;
    for (const r of rfids) {
      if (!(r in existingInfo)) { pending++; continue; }
      if (existingInfo[r] === null) nt.push(r);
      else et.push(existingInfo[r]);
    }
    return { newTags: nt, existingTags: et, pendingCount: pending };
  }, [rfids, existingInfo]);

  const toggleOverwrite = (rfid) => {
    setOverwriteSet((prev) => {
      const next = new Set(prev);
      if (next.has(rfid)) next.delete(rfid); else next.add(rfid);
      return next;
    });
  };

  const submitCount = newTags.length + pendingCount + overwriteSet.size;

  const onSubmit = async () => {
    if (!productId || rfids.size === 0 || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/linen/bulk-register`, {
        product_id: productId,
        codes: Array.from(rfids),
        notes: notes.trim() || undefined,
        overwrite_rfids: Array.from(overwriteSet),
      });
      beep.complete();
      const parts = [`Registered ${res.data.registered} new tag(s)`];
      if (res.data.overwritten) parts.push(`${res.data.overwritten} re-registered`);
      if (res.data.skipped_already_exists) parts.push(`${res.data.skipped_already_exists} kept as-is`);
      toast.success(parts.join(" · "));
      clearRfids();
      setNotes("");
    } catch (e) {
      beep.error();
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Registration failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PdaLayout
      title="Register"
      subtitle={product ? product.name : "Tag new linen"}
      onBack={() => navigate("/pda/menu")}
      onLogout={onLogout}
      footer={
        productId ? (
          <button
            onClick={onSubmit}
            disabled={submitting || submitCount === 0}
            data-testid="pda-register-submit"
            className="w-full h-12 rounded-md bg-rose-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              `Register ${newTags.length + pendingCount} new${overwriteSet.size ? ` · overwrite ${overwriteSet.size}` : ""}`
            )}
          </button>
        ) : null
      }
    >
      {!productId ? (
        // ─── Step 1: pick product ───
        <div className="rounded-md border bg-white" data-testid="pda-register-product-picker">
          <div className="px-3 py-2 border-b flex items-center gap-2">
            <Tag className="h-4 w-4 text-neutral-500" />
            <span className="text-xs font-bold">Pick the product to tag</span>
          </div>
          {productsLoading ? (
            <div className="px-3 py-3 text-xs text-neutral-500"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-2" />Loading…</div>
          ) : (
            <>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="pda-register-search"
                placeholder="Search products…"
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              <div className="max-h-96 overflow-y-auto">
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProductId(p.id)}
                    data-testid={`pda-register-product-${p.id}`}
                    className="w-full text-left px-3 py-3 hover:bg-rose-50 border-b last:border-b-0"
                  >
                    <div className="font-medium text-sm">{p.name}</div>
                    {p.description ? <div className="text-[11px] text-neutral-500 truncate">{p.description}</div> : null}
                  </button>
                ))}
                {filtered.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-neutral-500">No matches</div>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : (
        // ─── Step 2: scan + notes ───
        <div className="space-y-3">
          <button
            onClick={() => { setProductId(null); clearRfids(); setNotes(""); }}
            data-testid="pda-register-change-product"
            className="text-xs text-rose-700 underline"
          >
            ← Change product
          </button>

          <div className="rounded-md border bg-white px-3 py-4 text-center">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Scanned</div>
            <div className="font-display font-bold text-5xl text-neutral-900 mt-1" data-testid="pda-register-count">{rfids.size}</div>
            <div className="flex items-center justify-center gap-3 text-xs mt-1">
              <span className="text-emerald-700 font-semibold" data-testid="pda-register-new-count">{newTags.length + pendingCount} new</span>
              {existingTags.length > 0 && (
                <span className="text-amber-700 font-semibold" data-testid="pda-register-existing-count">{existingTags.length} already registered</span>
              )}
            </div>
            <div className="text-xs text-neutral-500 mt-1">Press the middle key to scan</div>
            {rfids.size > 0 ? (
              <button onClick={clearRfids} data-testid="pda-register-clear" className="mt-2 text-xs text-rose-700 underline flex items-center gap-1 mx-auto">
                <RotateCcw className="h-3 w-3" /> Clear
              </button>
            ) : null}
          </div>

          {/* Already registered — kept untouched unless tapped to overwrite */}
          {existingTags.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50" data-testid="pda-register-existing-list">
              <div className="px-3 py-2 border-b border-amber-200">
                <div className="text-xs uppercase tracking-wider font-bold text-amber-900 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Already registered · {existingTags.length}
                </div>
                <div className="text-[11px] text-amber-800 mt-0.5">
                  These keep their original info. Tap a tag to overwrite it as {product?.name || "the new product"}.
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-amber-200">
                {existingTags.map((it) => {
                  const ow = overwriteSet.has(it.rfid);
                  return (
                    <button
                      key={it.rfid}
                      onClick={() => toggleOverwrite(it.rfid)}
                      data-testid={`pda-register-existing-${it.rfid}`}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 ${ow ? "bg-rose-100" : ""}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-mono truncate">{it.rfid}</div>
                        <div className="text-[11px] text-amber-900">
                          {it.product_name}
                          {it.status === "at_hotel" && it.current_hotel_name ? ` · at ${it.current_hotel_name}` : it.status === "at_warehouse" ? " · warehouse" : ""}
                        </div>
                        {ow && (
                          <div className="text-[10px] font-bold text-rose-700 flex items-center gap-1 mt-0.5">
                            <RefreshCw className="h-3 w-3" /> Will be re-registered as {product?.name}
                          </div>
                        )}
                      </div>
                      <div className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded ${ow ? "bg-rose-600 text-white" : "bg-white border border-amber-300 text-amber-800"}`}>
                        {ow ? "OVERWRITE" : "KEEP"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="rounded-md border bg-white px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Note (optional)</div>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="pda-register-notes"
              placeholder="e.g. batch 2026-02 supplier ABC"
              className="w-full text-sm focus:outline-none"
            />
          </div>

          {newTags.length + pendingCount > 0 && (
            <div className="rounded-md border bg-white">
              <div className="px-3 py-2 border-b text-xs uppercase tracking-wider text-neutral-600">New tags (last 20)</div>
              <div className="max-h-48 overflow-y-auto divide-y">
                {Array.from(rfids).filter((r) => existingInfo[r] === null || !(r in existingInfo)).slice(-20).reverse().map((r) => (
                  <div key={r} className="px-3 py-1.5 text-[11px] font-mono flex items-center gap-2">
                    <Check className="h-3 w-3 text-emerald-600 shrink-0" /> {r}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </PdaLayout>
  );
}
